import React, { Component } from 'react'
import { Alert, Col, Row, message } from 'antd'
import { tranzila } from '@services/api'
import SearchBar from './SearchBar'
import List from './List'
import AssignUserModal from './AssignUserModal'
import AssignSkuModal from './AssignSkuModal'
import {
    AUTO_MATCH_TEXT,
    AUTO_SKU_TEXT,
    MANUAL_ASSIGN_TEXT,
    MANUAL_SKU_TEXT,
    DEFAULT_PER_PAGE,
    autoMatchOutcome,
    buildUnresolvedPayload,
    cloneUnresolvedFilters,
    fallbackPageAfterReload,
    initialUnresolvedFilters,
    unresolvedError
} from './utils'

// The repair queue: cache rows whose customer or product code still needs a human. Its writes are
// accepting an AUTO_AVAILABLE user match (resolveUserAuto), assigning one instrument candidate
// to a MANUAL_USER_REQUIRED row (the assign* methods), and accepting the one local SKU of a
// MANUAL_SKU_REQUIRED row (resolveSkuAuto), and -- manual SKU V1 -- a code the operator explicitly
// selects and confirms for a manual_sku_available row (the *AssignSku methods); everything else
// is read-only.
//
// Its own component, its own endpoint, its own filters and its own state -- mounted by the
// Documents page only when its tab is first opened. The ordinary Documents browse must not pay
// for a predicate that joins users and reads an unindexed column, and an operator who never
// opens this tab never runs that query.
//
// The data flow is deliberately the SAME shape as the Documents page (draft filters, an applied
// snapshot captured on success, a generation counter, an unmount flag), because that shape has
// already been audited and the two tabs behaving differently would be its own bug.
class Unresolved extends Component<any, any> {
    // Filters and paging overlap easily; a slow earlier response must never overwrite a newer one.
    fetchSeq = 0
    // Candidate loads overlap when the modal is closed and reopened; only the latest may land.
    candidatesSeq = 0
    // Same rule for the sku-history load.
    skuHistorySeq = 0
    unmounted = false

    constructor(props) {
        super(props)
        this.state = {
            // DRAFT: what the operator is typing right now.
            filters: initialUnresolvedFilters(),
            // APPLIED: the filters the rows below were actually loaded with. null until the
            // first successful response.
            appliedFilters: null,
            rows: [],
            total: 0,
            page: 1,
            perPage: DEFAULT_PER_PAGE,
            loading: false,
            // Set on failure, cleared on success. The table itself is NOT emptied -- a failed
            // refresh must not throw away a page the operator is working through.
            failed: false,
            // The row whose match is being accepted/assigned; null when none is in flight.
            resolvingId: null,
            // The manual-assignment modal: null when closed, else
            // { row, loading, candidates, selected }. Never kept after close.
            assign: null,
            // The manual SKU modal: null when closed, else
            // { row, loading, evidence, selected, confirming }. `selected` starts null on EVERY open.
            skuAssign: null
        }
    }

    componentDidMount() {
        // ONE request, when the tab is first opened. Nothing fetches this list on the Documents
        // page's own load, because this component does not exist until then.
        this.fetchRows(1)
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    // `options.fallbackToLastPage`: used only by the reload after a fix. If the page asked for no
    // longer exists, its empty answer is never shown -- the server's last page is loaded instead.
    fetchRows = (page = this.state.page, filters = this.state.filters, options: any = {}) => {
        this.fetchSeq += 1
        const seq = this.fetchSeq
        // The request is built from a SNAPSHOT, so an edit made while it is in flight can change
        // neither what was asked for nor what is recorded as applied.
        const requested = cloneUnresolvedFilters(filters)
        this.setState({ loading: true })

        tranzila
            .getDocumentsUnresolved(buildUnresolvedPayload(requested, page, this.state.perPage))
            .then(res => {
                if (this.unmounted || seq !== this.fetchSeq) return
                const payload = (res && res.data) || {}
                // The page's tab badge is THIS number: no second count query exists, and none
                // runs during the ordinary Documents load.
                if (typeof this.props.onTotal === 'function') this.props.onTotal(Number(payload.total) || 0)
                const fallback = options.fallbackToLastPage
                    ? fallbackPageAfterReload(page, payload, this.state.perPage)
                    : null
                if (fallback !== null) {
                    this.fetchRows(fallback, requested)
                    return
                }
                // ONE setState, so the snapshot and the rows it describes can never disagree.
                this.setState({
                    rows: Array.isArray(payload.data) ? payload.data : [],
                    total: Number(payload.total) || 0,
                    page: Number(payload.page) || page,
                    perPage: Number(payload.per_page) || this.state.perPage,
                    appliedFilters: cloneUnresolvedFilters(requested),
                    loading: false,
                    failed: false
                })
            })
            .catch(err => {
                if (this.unmounted || seq !== this.fetchSeq) return
                // Only `loading` and `failed` move: the table, its total and its applied snapshot
                // all stay as they were. Same stale-display rule as the Documents tab.
                this.setState({ loading: false, failed: true })
                const data = err && err.response && err.response.data
                message.error(unresolvedError(data))
            })
    }

    setFilter = (name, value) => {
        this.setState(prev => ({ filters: Object.assign({}, prev.filters, { [name]: value }) }))
    }

    handleSearch = () => {
        this.setState({ page: 1 }, () => this.fetchRows(1))
    }

    handleReset = () => {
        const filters = initialUnresolvedFilters()
        this.setState({ filters, page: 1 }, () => this.fetchRows(1, filters))
    }

    // Accept the automatic user match for one row. One request at a time; afterwards the SAME
    // dataset (applied filters, current page -- or the new last page if this one emptied) is
    // re-read, which also moves the tab badge.
    // A 409 means the row changed server-side: say so and re-read. Any other failure keeps the
    // table exactly as it is.
    // The SAME dataset after a fix: applied filters, current page -- or the new last page.
    reloadAfterFix = () =>
        this.fetchRows(this.state.page, this.state.appliedFilters || this.state.filters, {
            fallbackToLastPage: true
        })

    resolveUserAuto = row => {
        if (!row || this.state.resolvingId !== null) return
        this.setState({ resolvingId: row.id })
        const reload = this.reloadAfterFix

        // The SAME candidate the confirmation named (autoMatchConfirmText) is the precondition.
        tranzila
            .resolveDocumentUserAuto(row.id, row.auto_resolve_user_id)
            .then(() => {
                if (this.unmounted) return
                this.setState({ resolvingId: null })
                message.success(AUTO_MATCH_TEXT.success)
                reload()
            })
            .catch(err => {
                if (this.unmounted) return
                this.setState({ resolvingId: null })
                if (autoMatchOutcome(err) === 'changed') {
                    message.warning(AUTO_MATCH_TEXT.changed)
                    reload()
                } else message.error(AUTO_MATCH_TEXT.error)
            })
    }

    // Same contract as resolveUserAuto, for the row's one local SKU: one request at a time,
    // success/409 -> re-read, anything else keeps the row as it is.
    resolveSkuAuto = row => {
        if (!row || this.state.resolvingId !== null) return
        this.setState({ resolvingId: row.id })

        tranzila
            .resolveDocumentSkuAuto(row.id)
            .then(() => {
                if (this.unmounted) return
                this.setState({ resolvingId: null })
                message.success(AUTO_SKU_TEXT.success)
                this.reloadAfterFix()
            })
            .catch(err => {
                if (this.unmounted) return
                this.setState({ resolvingId: null })
                if (autoMatchOutcome(err) === 'changed') {
                    message.warning(AUTO_SKU_TEXT.changed)
                    this.reloadAfterFix()
                } else message.error(AUTO_SKU_TEXT.error)
            })
    }

    // Opens the modal and loads the row's CURRENT candidates. Nothing from the list row is used as
    // a candidate; a 409 means the row is no longer MANUAL_USER_REQUIRED -> close and re-read.
    openAssignUser = row => {
        if (!row || this.state.resolvingId !== null) return
        this.candidatesSeq += 1
        const seq = this.candidatesSeq
        this.setState({ assign: { row, loading: true, candidates: [], selected: null } })
        const current = () => !this.unmounted && seq === this.candidatesSeq && this.state.assign !== null

        tranzila
            .getDocumentUserCandidates(row.id)
            .then(res => {
                if (!current()) return
                const payload = (res && res.data) || {}
                const candidates = Array.isArray(payload.candidates) ? payload.candidates : []
                this.setState({ assign: Object.assign({}, this.state.assign, { loading: false, candidates }) })
            })
            .catch(err => {
                if (!current()) return
                this.closeAssignUser()
                if (autoMatchOutcome(err) === 'changed') {
                    message.warning(MANUAL_ASSIGN_TEXT.changed)
                    this.reloadAfterFix()
                } else message.error(MANUAL_ASSIGN_TEXT.loadError)
            })
    }

    selectAssignCandidate = userId => {
        if (!this.state.assign || this.state.resolvingId !== null) return
        this.setState({ assign: Object.assign({}, this.state.assign, { selected: userId }) })
    }

    // Closing also orphans an in-flight candidate load (candidatesSeq), so it can never reopen it.
    closeAssignUser = () => {
        if (this.state.resolvingId !== null) return
        this.candidatesSeq += 1
        this.setState({ assign: null })
    }

    // Same contract as resolveUserAuto: one request at a time, success/409 -> re-read, anything
    // else keeps the modal (and the row) so the operator can retry or cancel.
    assignUser = () => {
        const assign = this.state.assign
        if (!assign || assign.loading || !assign.selected || this.state.resolvingId !== null) return
        const row = assign.row
        this.setState({ resolvingId: row.id })

        tranzila
            .resolveDocumentUserManual(row.id, assign.selected)
            .then(() => {
                if (this.unmounted) return
                this.setState({ resolvingId: null, assign: null })
                message.success(MANUAL_ASSIGN_TEXT.success)
                this.reloadAfterFix()
            })
            .catch(err => {
                if (this.unmounted) return
                if (autoMatchOutcome(err) === 'changed') {
                    this.setState({ resolvingId: null, assign: null })
                    message.warning(MANUAL_ASSIGN_TEXT.changed)
                    this.reloadAfterFix()
                } else {
                    this.setState({ resolvingId: null })
                    message.error(MANUAL_ASSIGN_TEXT.error)
                }
            })
    }

    // MANUAL SKU V1. Opens the modal EMPTY and loads the row's advisory history. Nothing in the
    // response selects a code: `selected` stays null until the operator picks one.
    openAssignSku = row => {
        if (!row || this.state.resolvingId !== null) return
        this.skuHistorySeq += 1
        const seq = this.skuHistorySeq
        this.setState({ skuAssign: { row, loading: true, evidence: null, selected: null, confirming: false } })
        const current = () => !this.unmounted && seq === this.skuHistorySeq && this.state.skuAssign !== null

        tranzila
            .getDocumentSkuHistory(row.id)
            .then(res => {
                if (!current()) return
                const evidence = (res && res.data) || {}
                this.setState({ skuAssign: Object.assign({}, this.state.skuAssign, { loading: false, evidence }) })
            })
            .catch(err => {
                if (!current()) return
                this.closeAssignSku()
                if (autoMatchOutcome(err) === 'changed') {
                    message.warning(MANUAL_SKU_TEXT.changed)
                    this.reloadAfterFix()
                } else message.error(MANUAL_SKU_TEXT.loadError)
            })
    }

    // The operator's own choice -- from the picker, or the hint's explicit "בחר" button.
    selectAssignSku = sku => {
        const skuAssign = this.state.skuAssign
        if (!skuAssign || skuAssign.confirming || this.state.resolvingId !== null) return
        this.setState({ skuAssign: Object.assign({}, skuAssign, { selected: sku || null }) })
    }

    // First step: a chosen code moves to the second confirmation. Never a request.
    requestSkuConfirm = () => {
        const skuAssign = this.state.skuAssign
        if (!skuAssign || skuAssign.loading || !skuAssign.selected) return
        this.setState({ skuAssign: Object.assign({}, skuAssign, { confirming: true }) })
    }

    backFromSkuConfirm = () => {
        const skuAssign = this.state.skuAssign
        if (!skuAssign || this.state.resolvingId !== null) return
        this.setState({ skuAssign: Object.assign({}, skuAssign, { confirming: false }) })
    }

    closeAssignSku = () => {
        if (this.state.resolvingId !== null) return
        this.skuHistorySeq += 1
        this.setState({ skuAssign: null })
    }

    // The ONLY manual-sku write: from the second confirmation, with the selected code as the whole
    // body. Same contract as assignUser: success/409 -> close and re-read, else keep the modal.
    assignSku = () => {
        const skuAssign = this.state.skuAssign
        if (!skuAssign || !skuAssign.confirming || !skuAssign.selected || this.state.resolvingId !== null) return
        const row = skuAssign.row
        this.setState({ resolvingId: row.id })

        tranzila
            .resolveDocumentSkuManual(row.id, skuAssign.selected)
            .then(() => {
                if (this.unmounted) return
                this.setState({ resolvingId: null, skuAssign: null })
                message.success(MANUAL_SKU_TEXT.success)
                this.reloadAfterFix()
            })
            .catch(err => {
                if (this.unmounted) return
                if (autoMatchOutcome(err) === 'changed') {
                    this.setState({ resolvingId: null, skuAssign: null })
                    message.warning(MANUAL_SKU_TEXT.changed)
                    this.reloadAfterFix()
                } else {
                    this.setState({ resolvingId: null })
                    message.error(MANUAL_SKU_TEXT.error)
                }
            })
    }

    // Another page of the SAME dataset: the applied snapshot is reused, so turning a page can
    // never apply unsent draft edits.
    handlePageChange = page => this.fetchRows(page, this.state.appliedFilters || this.state.filters)

    render() {
        const {
            filters,
            rows,
            total,
            page,
            perPage,
            loading,
            appliedFilters,
            failed,
            resolvingId,
            assign,
            skuAssign
        } = this.state
        // Three distinct states, not two: still loading, loaded and empty, or failed. An empty
        // queue is good news and must not read like a broken page.
        const settled = !loading && appliedFilters !== null
        const empty = settled && !failed && total === 0

        return (
            <div>
                <Row gutter={10}>
                    <Col>
                        <SearchBar
                            filters={filters}
                            setFilter={this.setFilter}
                            onSearch={this.handleSearch}
                            onReset={this.handleReset}
                            loading={loading}
                        />
                    </Col>
                </Row>

                {failed ? (
                    <Row gutter={10}>
                        <Col style={{ marginBottom: '1rem' }}>
                            <Alert
                                type="error"
                                showIcon
                                message="טעינת הרשימה נכשלה"
                                description="הנתונים המוצגים הם מהטעינה האחרונה שהצליחה. ניתן לנסות שוב."
                            />
                        </Col>
                    </Row>
                ) : null}

                {empty ? (
                    <Row gutter={10}>
                        <Col style={{ marginBottom: '1rem' }}>
                            <Alert type="success" showIcon message="אין רשומות הדורשות טיפול" />
                        </Col>
                    </Row>
                ) : null}

                <Row gutter={10}>
                    <Col style={{ minWidth: 0 }}>
                        <List
                            rows={rows}
                            loading={loading}
                            page={page}
                            perPage={perPage}
                            total={total}
                            onPageChange={this.handlePageChange}
                            onOpenTransactionDetails={this.props.onOpenTransactionDetails}
                            detailsLoadingId={this.props.detailsLoadingId}
                            onResolveUserAuto={this.resolveUserAuto}
                            onAssignUser={this.openAssignUser}
                            onResolveSkuAuto={this.resolveSkuAuto}
                            onAssignSku={this.openAssignSku}
                            resolvingId={resolvingId}
                        />
                    </Col>
                </Row>

                <AssignUserModal
                    row={assign ? assign.row : null}
                    loading={!!assign && assign.loading}
                    candidates={assign ? assign.candidates : []}
                    selected={assign ? assign.selected : null}
                    submitting={!!assign && resolvingId !== null && resolvingId === assign.row.id}
                    onSelect={this.selectAssignCandidate}
                    onCancel={this.closeAssignUser}
                    onConfirm={this.assignUser}
                />

                <AssignSkuModal
                    row={skuAssign ? skuAssign.row : null}
                    loading={!!skuAssign && skuAssign.loading}
                    evidence={skuAssign ? skuAssign.evidence : null}
                    selected={skuAssign ? skuAssign.selected : null}
                    confirming={!!skuAssign && skuAssign.confirming}
                    submitting={!!skuAssign && resolvingId !== null && resolvingId === skuAssign.row.id}
                    onSelect={this.selectAssignSku}
                    onPickHint={this.selectAssignSku}
                    onRequestConfirm={this.requestSkuConfirm}
                    onBack={this.backFromSkuConfirm}
                    onCancel={this.closeAssignSku}
                    onConfirm={this.assignSku}
                />
            </div>
        )
    }
}

export default Unresolved
